import React, { useEffect, useState } from 'react'
import BreadCrumbs from '../../../components/BreadCrumbs';
import { Alert, Card, CardBody, Col, Row } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLazyGetRefundOrdersQuery } from '../../../redux/services/dashboardService';
import TableLoadingComponent from '../../../components/TableLoadingComponent';
import { setRowsPerPage } from '../../../redux/refundOrderSlice';
import ViewButtonComponent from '../../../components/ViewButtonComponent';
import Select from "react-select";
import { REFUND_ORDER_STATUS } from '../../../constants/constans';
import { Badge, Label } from 'reactstrap';

export interface RefundOrder {
    refundRequestsList: {
        amount?: number;
        orderId: string;
        orderReference: string;
        description: string;
        requestedDate: string;
        status: string;
        requestedBy: string;
        customer: {
            userId: string;
            id: string;
            displayName?: string;
            email?: string;
        };
        createdAt: {
            _seconds: number;
            _nanoseconds: number;
        };
        id: string;
    }[];
}

const OrderRefund = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const restaurantId = useSelector(
        (state: any) => state.restaurant.selectedRestaurantDetails.id
    );

    const rowsPerPage = useSelector((state: any) => state.refundOrder.rowsPerPage);
    const [orders, setOrders] = useState<RefundOrder['refundRequestsList']>([]);
    const [selectedStatus, setSelectedStatus] = useState<string>("All");

    const [triggerRefundOrders, { data, isLoading, error }] = useLazyGetRefundOrdersQuery();

    useEffect(() => {
        triggerRefundOrders({ restaurantId, status: selectedStatus });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerRefundOrders, restaurantId]);

    useEffect(() => {
        if (data && data.refundRequestsList) {
            setOrders(data.refundRequestsList);
        }
    }, [data]);

    const handleStatusChange = (selectedOption: { value: string; label: string } | null) => {
        const newStatus = selectedOption ? selectedOption.value : "All";
        setSelectedStatus(newStatus);
        triggerRefundOrders({ restaurantId, status: newStatus });
    };

    const handleViewRefundOrder = (order: RefundOrder['refundRequestsList'][0]) => {
        navigate('/payments/refund-orders/view-order-refund', { state: { order } });
    };

    const handleChangeRowsPerPage = (newRowsPerPage: number) => {
        dispatch(setRowsPerPage(newRowsPerPage));
    };

    const columns: TableColumn<RefundOrder['refundRequestsList'][0]>[] = [
        {
            name: <h6 className="fw-bold">Order Reference</h6>,
            selector: (row) => row.orderReference,
            width: '20%',
        },
        {
            name: <h6 className="fw-bold">Requested Date</h6>,
            selector: (row) =>
                new Date(row.createdAt._seconds * 1000).toLocaleDateString(),
            width: '15%',
        },
        {
            name: <h6 className="fw-bold">Customer Name</h6>,
            selector: (row) => row.customer.displayName || '',
            width: '15%',
        },
        {
            name: <h6 className="fw-bold">Description</h6>,
            selector: (row) => row.description,
            width: '20%',
        },
        {
            name: <h6 className="fw-bold">Amount (Rs.)</h6>,
            selector: (row) => row.amount || 0,
            width: '10%',
        },
        {
            name: <h6 className="fw-bold">Status</h6>,
            cell: (row) => (
                <div>
                    <Badge
                        color={
                            row?.status === "Pending"
                                ? "warning"
                                : row?.status === "Rejected"
                                    ? "danger"
                                    : row?.status === "Completed"
                                        ? "success"
                                        : "secondary"
                        }
                        className="px-4 py-2"
                    >
                        <div style={{ fontSize: 12 }}>
                            {row?.status === "Pending"
                                ? "Pending"
                                : row?.status === "Rejected"
                                    ? "Rejected"
                                    : row?.status === "Completed"
                                        ? "Completed"
                                        : "Cancelled"}
                        </div>
                    </Badge>
                </div>
            ),
            width: '10%',
        },
        {
            name: <h6 className="fw-bold">Action</h6>,
            cell: (row) => (
                <div className="d-flex gap-2">
                    <ViewButtonComponent onClick={() => handleViewRefundOrder(row)} />
                </div>
            ),
            width: '10%',
            right: true,
        },
    ];

    return (
        <>
            <BreadCrumbs hasSubMenus={true} />
            <Row className='mt-3 mb-3 d-flex justify-content-between'>
                <Col lg="4">
                    <h4>Customer Refunds</h4>
                </Col>
                <Col lg="4" className="mb-3">

                </Col>
                <Col lg="4" className="mb-3 d-flex flex-column align-items-start">
                    <Label>Refund Status</Label>
                    <Select
                        options={REFUND_ORDER_STATUS}
                        onChange={handleStatusChange}
                        value={REFUND_ORDER_STATUS.find(option => option.value === selectedStatus)}
                        className='w-100'
                    />
                </Col>
            </Row>
            <Card>
                <CardBody className="p-3">
                    {isLoading ? (
                        <TableLoadingComponent numColumns={5} />
                    ) : error ? (
                        <Alert variant="danger">Failed to load orders</Alert>
                    ) : (
                        <>
                            <DataTable
                                columns={columns}
                                data={orders}
                                pagination
                                highlightOnHover
                                noDataComponent={<div className='p-5'>No refund requests available</div>}
                                responsive
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                paginationPerPage={rowsPerPage}
                            />
                        </>
                    )}
                </CardBody>
            </Card>
        </>
    );
};

export default OrderRefund;
